import type { TaskDependency } from '@hive/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from './keys';

// Task dependencies: blocker_id has to be done before blocked_id. The database
// refuses loops and pairs from different games.

export type Dependency = Pick<TaskDependency, 'id' | 'blocker_id' | 'blocked_id'>;

/** Every dependency in the studio (a few hundred at most), shared by Grid's views and the drawer. */
export function useDependencies() {
  return useQuery({
    queryKey: queryKeys.dependencies,
    queryFn: async (): Promise<Dependency[]> => {
      const { data, error } = await supabase().from('task_dependencies').select('id, blocker_id, blocked_id');
      if (error) throw error;
      return data;
    },
  });
}

export function useAddDependency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ blockerId, blockedId }: { blockerId: string; blockedId: string }) => {
      const { error } = await supabase()
        .from('task_dependencies')
        .insert({ blocker_id: blockerId, blocked_id: blockedId });
      if (error) throw error;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.dependencies }),
  });
}

export function useRemoveDependency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase().from('task_dependencies').delete().eq('id', id);
      if (error) throw error;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.dependencies }),
  });
}

/** Tasks held up by an unfinished blocker. */
export function blockedTaskIds(
  dependencies: readonly Dependency[],
  tasks: readonly { id: string; status: string }[],
): Set<string> {
  const status = new Map(tasks.map((t) => [t.id, t.status]));
  const blocked = new Set<string>();
  for (const d of dependencies) {
    const blockerStatus = status.get(d.blocker_id);
    if (blockerStatus !== undefined && blockerStatus !== 'done' && status.has(d.blocked_id)) blocked.add(d.blocked_id);
  }
  return blocked;
}
